import React, { useState } from "react";
import { ArrowShapeDownToLine, Xmark } from "@gravity-ui/icons";
import { Clock } from "@gravity-ui/icons";
import apiService from "../../api/apiService";
import { usePostHog } from "posthog-js/react";
import * as Sentry from "@sentry/react"
const DownloadModal = ({
  title,
  description,
  duration,
  imageSrc,
  onClose,
  ai_response_id,
  onError,
  trackIndex
}) => {
  const [fileType, setFileType] = useState("WAV");
  const [isDownloading, setIsDownloading] = useState(false);
  const posthog = usePostHog();

  const handleDownload = async () => {
    let effectiveTrackIndex = trackIndex;
    posthog.capture("Click to Download Audio",{is_user_logged_in: true}, {trackIndex}, {fileType});
    console.log('[DownloadModal] Starting download process:', {
      ai_response_id,
      fileType,
      isDownloading,
      title,
      receivedTrackIndex: trackIndex
    });

    if (effectiveTrackIndex === undefined || effectiveTrackIndex === null || isNaN(parseInt(effectiveTrackIndex))) {
      console.warn(`[DownloadModal] Invalid or missing trackIndex received: ${trackIndex}. Defaulting to track 0.`);
      effectiveTrackIndex = 0;
    } else {
      effectiveTrackIndex = parseInt(effectiveTrackIndex, 10);
    }

    if (isNaN(effectiveTrackIndex)) {
        console.error('[DownloadModal] Processed effectiveTrackIndex is NaN. Aborting download.');
        onError?.('Cannot download: Critical error with track selection.');
        setIsDownloading(false);
        return;
    }

    try {
      setIsDownloading(true);
      console.log('[DownloadModal] Set downloading state to true');

      console.log('[DownloadModal] Calling apiService.downloadAudio with params:', {
        passed_ai_response_id: ai_response_id,
        passed_fileType: fileType,
        passed_trackId: effectiveTrackIndex
      });

      const downloadResult = await apiService.downloadAudio({
        ai_response_id: ai_response_id,
        fileType: fileType,
        trackId: effectiveTrackIndex,
        songTitle: title,
      });

      console.log('[DownloadModal] apiService.downloadAudio completed.', downloadResult);

      console.log('[DownloadModal] Download initiated successfully by apiService.');
      onClose();
    } catch (error) {
      console.error('[DownloadModal] Download error:', {
        message: error.message,
        error
      });
      Sentry.captureException(error);
      const errorMessage = error.message || 'Failed to download audio. Please try again.';
      onError?.(errorMessage);
    } finally {
      console.log('[DownloadModal] Cleaning up - setting downloading state to false');
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-[rgba(0,0,0,0.7)]">
      <div className="w-full max-w-[520px] flex flex-col bg-[#1c1b1c] rounded-lg border border-[#4d4a4e] relative">
        <div className="flex items-center justify-between p-3 border-b border-[#2c2a2c]">
          <h2 className="text-lg text-white">Download Track</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[rgba(255,255,255,0.05)]"
          >
            <Xmark className="w-4 h-4 text-[#878188]" />
          </button>
        </div>

        <div className="p-4 flex flex-col gap-4">
          {/* Track Preview */}
          <div className="flex gap-3 p-2 items-center rounded-xl border border-[rgba(255,255,255,0.2)]">
            <img
              src={imageSrc}
              alt={title}
              className="w-20 h-20 object-cover rounded"
            />
            <div className="flex flex-col flex-1 min-w-0">
              <h3 className="text-lg font-medium text-white truncate">
                {title}
              </h3>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#a5a0a6] truncate">
                  {description}
                </span>
                <div className="flex items-center gap-1 text-[#a5a0a6]">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-sm">{duration}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="flex flex-col gap-3">
            {/* File Type */}
            <div className="flex items-center gap-2">
              <span className="w-22 text-sm text-white">File Type</span>
              <div className="bg-[rgba(255,255,255,0.1)] rounded-lg p-0.5">
                <button
                  onClick={() => setFileType("WAV")}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    fileType === "WAV"
                      ? "bg-[rgba(255,255,255,0.1)] text-white"
                      : "text-[#878188]"
                  }`}
                >
                  WAV
                </button>
                <button
                  onClick={() => setFileType("MP3")}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    fileType === "MP3"
                      ? "bg-[rgba(255,255,255,0.1)] text-white"
                      : "text-[#878188]"
                  }`}
                >
                  MP3
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end mt-2">
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className={`flex items-center gap-2 px-3 h-9 rounded-full transition-colors ${
                isDownloading 
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-white hover:bg-gray-100'
              }`}
            >
              {isDownloading ? (
                <div className="w-4 h-4 border-2 border-t-2 border-black border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <ArrowShapeDownToLine className="w-4 h-4 text-black" />
              )}
              <span className="text-sm font-medium text-[#0f0e0f]">
                {isDownloading ? 'Downloading...' : 'Download'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;
